import { Alert, Col, Form, FormLabel, Row } from "react-bootstrap";
import AppModal from "../../components/common/AppModal";
import HTMLEditor from "../../components/common/HTMLEditor";
import SiteMediaPicker from "../../components/common/SiteMediaPicker";
import { SocialMediaPost } from "../../../services/socialmedia/SocialMediaPost";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { useEffect, useRef, useState } from "react";
import { ShareTo } from "../../../config/Enums";
import { IShareSocialMediaItem, ISiteDetails } from "../../../models/Interfaces";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import { faShareAlt, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../components/common/Loading";
import { EventBus } from "../../../helpers/EventBus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faGoogle, faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";

interface IShareSociaMedia {
  pageurl: string;
  sitedetails: ISiteDetails | null;
  closeCallback: () => void;
}

const ShareSocialMedia = (props: IShareSociaMedia) => {
  const [shareItem, setShareItem] = useState<IShareSocialMediaItem | null>(null);
  const [availableSM, setAvailableSM] = useState<Array<ShareTo>>([]);
  const [selectedSM, setSelectedSM] = useState<Array<ShareTo>>([]);
  const [sharing, setSharing] = useState<boolean>(false);
  const [shared, setShared] = useState<Array<ShareTo>>([]);
  const [shareComplete, setShareComplete] = useState<boolean>(false);
  const [shareFailed, setShareFailed] = useState<Array<ShareTo>>([]);
  const [sharePending, setSharePending] = useState<Array<ShareTo>>([]);
  const socialMediaPost = useRef<SocialMediaPost | null>(null);
  const shareItemRef = useRef<IShareSocialMediaItem | null>(null);

  const shareToTitle = (smitem: ShareTo | null): string => {
    if (smitem) {
      return `Share To <b>${ShareTo[smitem]}</b>`;
    }
    return "Share Social Media";
  };

  const shareVersion = async (): Promise<void> => {
    if (selectedSM.length > 0 && shareItem && socialMediaPost.current) {
      setSharing(true);
      setShareComplete(false);
      setShared([]);
      setShareFailed([]);
      setSharePending([]);

      for (const item of selectedSM.sort((a, b) => a - b)) {
        try {
          await shareToSocialMedia(shareItem, item);
        } catch (error) {
          setShareFailed(prev => [...prev, item])
        }
      }
      setShareComplete(true);
      setSharing(false);

    } else {
      actionEmitter.emit(EmitEvent.Result, false, "No social media services selected or missing data");
    }
  };

  const shareToSocialMedia = async (smpost: IShareSocialMediaItem, item: ShareTo): Promise<void> => {
    if (smpost && socialMediaPost.current) {
      try {
        const res = await socialMediaPost.current.shareVersionContent(smpost, item);
        if (res.status === "success") {
          setSharePending(prev => prev.filter(sm => sm !== item));
          setShareFailed(prev => prev.filter(sm => sm !== item));
          setShared(prev => prev.includes(item) ? prev : [...prev, item]);
        } else if (res.status === "pending") {
          setSharePending(prev => prev.includes(item) ? prev : [...prev, item]);
        } else {
          setSharePending(prev => prev.filter(sm => sm !== item));
          setShareFailed(prev => [...prev, item]);
        }
      } catch (error) {
        setSharePending(prev => prev.filter(sm => sm !== item));
        setShareFailed(prev => [...prev, item])
      }
    } else {
      setShareFailed(prev => [...prev, item]);
    }
  };

  const getFullPageUrl = (): string => {
    let url: string = "";

    if (props.sitedetails) {
      if (props.pageurl && props.pageurl) {
        let pageUrl = props.pageurl === "/" ? "" : props.pageurl;

        if (props.sitedetails.Domain) {
          url = `https://www.${props.sitedetails.Domain}${pageUrl}`;
        } else {
          url = `https://${props.sitedetails.SubDomain}.smoothbadgersites.com${pageUrl}`;
        }
      }
    }
    return url;
  };

  const closeShareVersion = (): void => {
    setShareItem(null);
    if (props.closeCallback) {
      props.closeCallback();
    }
  };

  const handleShareItemChange = (propname: string, value: any): void => {

    if (propname && shareItem) {
      setShareItem(prev => ({
        ...prev as IShareSocialMediaItem,
        [propname]: value
      }))
    }
  };

  const handleSMClick = (item: ShareTo, checked: boolean) : void => {
    setSelectedSM((prev) => {
      if (checked) {
        // Add the item if not already present
        return prev.includes(item) ? prev : [...prev, item];
      } else {
        // Remove the item if it exists
        return prev.filter((sm) => sm !== item);
      }
    });
  };

  const getSMIcon = (item: ShareTo) : IconDefinition => {
    var ico: IconDefinition;

    switch (item) {
      case ShareTo.Facebook:
        ico = faFacebook;
        break;
      case ShareTo.Google:
        ico = faGoogle;
        break;
      case ShareTo.Instagram:
        ico = faInstagram;
        break;
      case ShareTo.X:
        ico = faXTwitter;
        break;
    }

    return ico;
  };

  const getIconClass = (item: ShareTo) : string => {
    let cls: string = "";
    switch (item) {
      case ShareTo.Facebook:
        cls = "text-primary";
        break;
      case ShareTo.Google:
        cls = "text-danger";
        break;
      case ShareTo.Instagram:
        cls = "text-black";
        break;
      case ShareTo.X:
        cls = "bg-black text-white rounded";
        break;
    }
    return cls;
  };

  useEffect(() => {
    shareItemRef.current = shareItem;
  }, [shareItem]);

  useEffect(() => {
    const sharecount = shared.length + shareFailed.length;

    if (sharePending.length > 0) {
      return;
    }

    if (selectedSM.length > 0 && sharecount === selectedSM.length) {
      if (shared.length > 0) {
        const msg = shared.map(si => ShareTo[si]);
        if (msg.length > 0) {
          actionEmitter.emit(EmitEvent.Result, true, `Successfully Shared to ${msg.join(", ")}.`);
        }
      }

      if (shareFailed.length > 0) {
        const msg = shareFailed.map(si => ShareTo[si]);
        if (msg.length > 0) {
          actionEmitter.emit(EmitEvent.Result, false, `Sharing to ${msg.join(", ")} failed.`);
        }
      }

      setShareComplete(true);
    }
  }, [shareFailed, sharePending, shared, selectedSM]);

  useEffect(() => {
    if (!shareItem) {
      setShareItem({
        PostText: "",
        ImageCaption: "",
        ImageUrl: "",
        VersionUrl: getFullPageUrl(),
        IncludePageLink: true
      });
    }

    if (props.sitedetails) {
      if (props.sitedetails.ShareFacebook) {
        setAvailableSM(prev => prev.includes(ShareTo.Facebook) ? prev : [...prev, ShareTo.Facebook]);
      }

      if (props.sitedetails.ShareGoogle) {
        setAvailableSM(prev => prev.includes(ShareTo.Google) ? prev : [...prev, ShareTo.Google]);
      }

      if (props.sitedetails.ShareInstagram) {
        setAvailableSM(prev => prev.includes(ShareTo.Instagram) ? prev : [...prev, ShareTo.Instagram]);
      }

      if (props.sitedetails.ShareX) {
        setAvailableSM(prev => prev.includes(ShareTo.X) ? prev : [...prev, ShareTo.X]);
      }
    }
  }, [props.sitedetails]);

  useEffect(() => {
    const handleMessage = async (evt: MessageEvent) => {
      // Validate the origin for security
      if (evt.origin !== window.location.origin) {
        console.warn("Ignored message from untrusted origin:", evt.origin);
        return;
      }

      // Validate the event type
      if (evt.data?.event === EventBus.Event.OnSocialMediaPublishingConnected && evt.data?.success) {
        const type = evt.data.type;
        if (shareItemRef.current) {
          await shareToSocialMedia(shareItemRef.current, type);
        }
      }
    };

    if (!socialMediaPost.current) {
      socialMediaPost.current = new SocialMediaPost();
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <>
      <AppModal
        onClose={closeShareVersion}
        showButtons={true}
        size="lg"
        actionButtonVariant="success"
        cancelButtonVariant="primary"
        cancelButtonText="Cancel"
        dataActionButtonText="Share"
        actionButtonIcon={faShareAlt}
        title={shareToTitle(null)}
        onDataAction={shareVersion}
        enableActionButton={selectedSM.length > 0}>
        
        {shareItem ? 
          (
            <>
              <div hidden={sharing || shareComplete}>
                  <Row key="shareToOptionsRow">
                    {availableSM.map(item => (
                      <Col md={2} key={`share-${ShareTo[item]}`} className="text-start">
                        <FormLabel 
                          htmlFor={`share-${ShareTo[item]}`}
                          title={`Share to ${ShareTo[item]}`}>
                            <FontAwesomeIcon icon={getSMIcon(item)} size="2x" className={getIconClass(item)} style={{ cursor: "pointer" }} />
                        </FormLabel>
                        <FormCheckInput
                          className="form-check"
                          id={`share-${ShareTo[item]}`}
                          name={`share-${ShareTo[item]}`}
                          onChange={(e) => handleSMClick(item, e.target.checked)}
                          title={`Share to ${ShareTo[item]}`}
                        />
                      </Col>
                    ))}
                  </Row>
                  <Form id="ShareSocialMediaForm" className="mt-3" key="shareToForm">
                    <Row>
                      <Col md={12} className="mt-3">
                        <FormLabel htmlFor="PostText">Share Text</FormLabel>
                        <HTMLEditor
                          value={shareItem.PostText}
                          onChange={(content) => handleShareItemChange("PostText", content)}
                          name="PostText"
                          id="PostText"
                        />
                      </Col>
                    </Row>
                    {selectedSM.includes(ShareTo.X) && (
                      <Row>
                        <Col md={12} className="mt-3">
                          <Alert variant="info" className="mb-0">
                            X sharing opens the standard share window with the text and page link pre-filled. If you want to include an image, add it manually in X after the share window opens.
                          </Alert>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col md={12} className="mt-3">
                        <SiteMediaPicker
                          onMediaClear={() => handleShareItemChange("ImageUrl", "")}
                          onMediaSelect={(url) => handleShareItemChange("ImageUrl", url)}
                          existingUrl={shareItem.ImageUrl}
                          captionLabel={"Select media to post with above"}
                        />
                      </Col>
                    </Row>
                  </Form>
              </div>

              {sharing && !shareComplete && (
                <Loading Message="sharing to social media..." SpinSize="5x" key="LoadingMessage" />
              )}

              {shareComplete && (
                <Alert variant="info" className="p-3 m-3 text-center fw-bold">
                  <span>Social media posting complete</span>
                </Alert>
              )}
            </>
          )
          :
          (
            <Row key="AlertRow">
              <Col md={12}>
                <Alert
                  variant="info"
                  title="No social media connections configured"
                  className="text-center fw-bold">
                  No social media connections configured
                </Alert>
              </Col>
            </Row>
          )
        }
      </AppModal>
    </>
  );
}

export default ShareSocialMedia;
